import React, { useEffect, useState, useCallback } from 'react';
import { X, Calendar, MapPin, User, Wallet } from 'lucide-react';
import type { TransparencyProject, TransparencyTimelineUpdate, TransparencyComment } from '@yawi/types';
import { useServices } from '@/app/providers';
import { useAbility } from '@/modules/identity/application/ability';
import { ProjectTimeline } from './ProjectTimeline';
import { ProjectComments } from './ProjectComments';
import { AddUpdateModal } from './AddUpdateModal';
import { ProjectLinksSection } from './ProjectLinksSection';

const STATUS_LABEL: Record<string, string> = {
  PLANNING: 'Planeación',
  APPROVED: 'Aprobado',
  IN_PROGRESS: 'En progreso',
  PAUSED: 'Pausado',
  DELAYED: 'Retrasado',
  COMPLETED: 'Completado',
  CANCELLED: 'Cancelado',
};

const CATEGORY_LABEL: Record<string, string> = {
  MAINTENANCE: 'Mantenimiento',
  SECURITY: 'Seguridad',
  REMODELING: 'Remodelación',
  LANDSCAPING: 'Jardinería',
  INFRASTRUCTURE: 'Infraestructura',
  AMENITIES: 'Amenidades',
  TECHNOLOGY: 'Tecnología',
  OTHER: 'Otro',
};

const PRIORITY_LABEL: Record<string, string> = {
  LOW: 'Baja',
  MEDIUM: 'Media',
  HIGH: 'Alta',
  URGENT: 'Urgente',
};

interface ProjectDetailModalProps {
  project: TransparencyProject;
  onClose: () => void;
}

export const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({ project, onClose }) => {
  const { transparencyProjectRepo } = useServices();
  const ability = useAbility();
  const canManage = ability.can('manage', 'all') || ability.can('update', 'TransparencyProject');

  const [timeline, setTimeline] = useState<TransparencyTimelineUpdate[]>([]);
  const [comments, setComments] = useState<TransparencyComment[]>([]);
  const [isAddUpdateOpen, setIsAddUpdateOpen] = useState(false);

  const loadThread = useCallback(async () => {
    const [timelineData, commentsData] = await Promise.all([
      transparencyProjectRepo.getTimeline(project.id),
      transparencyProjectRepo.getComments(project.id),
    ]);
    setTimeline(timelineData);
    setComments(commentsData);
  }, [transparencyProjectRepo, project.id]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-5 border-b border-slate-100 shrink-0">
          <h3 className="font-bold text-slate-800 text-lg">Detalle del proyecto</h3>
          <button onClick={onClose}>
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
          {project.coverImageUrl && (
            <img src={project.coverImageUrl} alt={project.title} className="w-full h-48 object-cover rounded-lg" />
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
              {CATEGORY_LABEL[project.category] ?? project.category}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
              {PRIORITY_LABEL[project.priority] ?? project.priority}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              {STATUS_LABEL[project.status] ?? project.status}
            </span>
          </div>

          <h2 className="text-xl font-bold text-slate-800">{project.title}</h2>

          <div>
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span>Avance</span>
              <span>{project.progress}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-primary-500" style={{ width: `${project.progress}%` }} />
            </div>
          </div>

          <div className="prose prose-sm max-w-none">
            <p className="text-slate-700 whitespace-pre-wrap">{project.description}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm text-slate-600">
            {project.communityLocation && (
              <span className="flex items-center gap-1.5">
                <MapPin size={14} className="text-slate-400" /> {project.communityLocation}
              </span>
            )}
            {project.responsiblePerson && (
              <span className="flex items-center gap-1.5">
                <User size={14} className="text-slate-400" /> {project.responsiblePerson}
              </span>
            )}
            {project.startDate && (
              <span className="flex items-center gap-1.5">
                <Calendar size={14} className="text-slate-400" /> Inicio: {project.startDate}
              </span>
            )}
            {project.estimatedCompletionDate && (
              <span className="flex items-center gap-1.5">
                <Calendar size={14} className="text-slate-400" /> Estimado: {project.estimatedCompletionDate}
              </span>
            )}
            {project.budget && (
              <span className="flex items-center gap-1.5">
                <Wallet size={14} className="text-slate-400" /> Presupuesto: ${project.budget}
              </span>
            )}
          </div>

          <hr className="border-slate-100" />

          <ProjectTimeline
            updates={timeline}
            canAddUpdate={canManage}
            onAddUpdate={() => setIsAddUpdateOpen(true)}
          />

          <hr className="border-slate-100" />

          <ProjectLinksSection projectId={project.id} canManage={canManage} />

          <hr className="border-slate-100" />

          <ProjectComments
            projectId={project.id}
            comments={comments}
            commentsEnabled={project.commentsEnabled}
            questionsEnabled={project.questionsEnabled}
            onChanged={loadThread}
          />
        </div>
      </div>

      {isAddUpdateOpen && (
        <AddUpdateModal
          project={project}
          onClose={() => setIsAddUpdateOpen(false)}
          onSaved={loadThread}
        />
      )}
    </div>
  );
};
